import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import * as platformService from "../../services/platformService";

// Super Admin only: switch Finance, the Dealership (DMS) and Procurement on or off for this one vendor,
// with their add-ons. The API enforces the rules and this panel explains them:
//   * the Dealership needs Finance, and is only for car service businesses
//   * Finance cannot be switched off while the Dealership is on
//   * an add-on needs its module on
//   * Procurement works for any business and does not need Finance
//   * a module can only be switched on where its database has been set up
// Switching a module on also gives the vendor its starting setup (currencies, UAE VAT
// codes, payment terms, document numbering, approval workflows, lists).
const ORDER = ["FIN", "DMS", "PROC"];
const HINTS = {
  FIN: "General ledger, receivables, payables, cash & bank, tax. Any business.",
  DMS: "Vehicle sales: quotations, orders, chassis stock, delivery. Car service businesses only; needs Finance.",
  PROC: "Buying: RFQs, supplier quotations, awards to purchase orders, supplier portal. Any business. Also switches on Inventory purchasing.",
};

export default function ModuleEnablementPanel({ vendorId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [saving, setSaving] = useState(null); // `${module}.${feature}` being saved
  const [pending, setPending] = useState(null); // { module, key, label, next } for the message and the switch position
  const saved = useRef({}); // switches saved since the last full load, kept on screen even if a re-read answers with older data

  // silent: re-read in the background without swapping the list for a spinner (used after a switch).
  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const res = silent ? await platformService.getEnablement(vendorId, true) : await platformService.getEnablement(vendorId);
        if (res && res.success === false) throw new Error(res.message || "Something went wrong");
        if (silent) {
          Object.entries(saved.current).forEach(([k, v]) => {
            const [m, key] = k.split(".");
            const mod = res.data.modules[m];
            if (!mod) return;
            mod.switches[key] = v;
            mod.features = mod.features.map((f) => (f.key === key ? { ...f, is_enabled: v } : f));
          });
        } else {
          saved.current = {};
        }
        setState({ loading: false, error: null, data: res.data });
      } catch (err) {
        if (silent) return; // the switch itself already shows; keep what is on screen
        setState({ loading: false, error: err.message || "Something went wrong", data: null });
      }
    },
    [vendorId]
  );

  useEffect(() => {
    if (vendorId) load();
  }, [vendorId, load]);

  const toggle = async (module, feature, next) => {
    setSaving(`${module}.${feature.key}`);
    setPending({ module, key: feature.key, label: feature.label, next });
    try {
      const res = await platformService.setFeatureEnabled(vendorId, module, feature.key, next);
      if (res?.success === false) toast.error(res.message || "Something went wrong");
      else {
        saved.current[`${module}.${feature.key}`] = next;
        // Show the new switch at once from what was saved; do not wait for a re-read.
        setState((s) => {
          if (!s.data) return s;
          const mod = s.data.modules[module];
          return {
            ...s,
            data: {
              ...s.data,
              modules: {
                ...s.data.modules,
                [module]: {
                  ...mod,
                  switches: { ...mod.switches, [feature.key]: next },
                  features: mod.features.map((f) => (f.key === feature.key ? { ...f, is_enabled: next } : f)),
                },
              },
            },
          };
        });
        toast.success(`${feature.label} ${next ? "switched on" : "switched off"}`);
        load({ silent: true }); // the other module's state and blockers can change too
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setSaving(null);
      setPending(null);
    }
  };

  if (!vendorId) return null;

  return (
    <div className="bg-white rounded p-3 mb-3" style={{ border: "1px solid #E9ECEF" }}>
      <h6 className="mb-1">Finance, Dealership and Procurement modules</h6>
      <div className="text-muted mb-3" style={{ fontSize: 13 }}>
        Switching a module on gives the vendor its starting setup. The vendor's own admin then completes it in Finance Setup / Dealership Setup / Procurement Setup.
      </div>
      {pending && pending.key === "CORE" && pending.next ? (
        <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: 13, color: "#1E40AF", background: "#DBEAFE", borderRadius: 6, padding: "8px 12px" }} role="status">
          <Spinner animation="border" size="sm" />
          <span>
            Setting up {pending.label} for this vendor (currencies, tax codes, chart of accounts, numbering, approval workflows). This can take a minute or two; please keep this page open.
          </span>
        </div>
      ) : pending ? (
        <div className="d-flex align-items-center gap-2 mb-3" style={{ fontSize: 13, color: "#1E40AF" }} role="status">
          <Spinner animation="border" size="sm" />
          <span>Saving...</span>
        </div>
      ) : null}
      {state.loading ? (
        <Spinner animation="border" size="sm" />
      ) : state.error ? (
        <div className="text-danger d-flex align-items-center gap-2" style={{ fontSize: 14 }} role="alert">
          {state.error}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => load()}>
            Try again
          </button>
        </div>
      ) : (
        ORDER.filter((m) => state.data.modules[m]).map((m) => {
          const mod = state.data.modules[m];
          const coreOn = !!mod.switches.CORE;
          return (
            <div key={m} className="mb-3" style={{ border: "1px solid #F3F4F6", borderRadius: 8, padding: "8px 12px" }}>
              <div className="d-flex align-items-start justify-content-between py-1">
                <div className="pe-3">
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{mod.label}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {HINTS[m]}
                  </div>
                </div>
              </div>
              {mod.blockers.length ? (
                <div className="my-2" style={{ fontSize: 13, color: "#92400E", background: "#FEF3C7", borderRadius: 6, padding: "6px 10px" }} role="status">
                  {coreOn ? "Switched on but not usable: " : "Cannot be switched on yet: "}
                  {mod.blockers.join("; ")}
                </div>
              ) : null}
              {mod.features.map((f) => {
                const id = `mod-${m}-${f.key}`;
                const isCore = f.key === "CORE";
                const busy = saving === `${m}.${f.key}`;
                return (
                  <div key={f.key} className="d-flex align-items-center justify-content-between py-2" style={{ borderTop: "1px solid #F3F4F6", paddingLeft: isCore ? 0 : 16 }}>
                    <label htmlFor={id} style={{ fontSize: 14, fontWeight: isCore ? 600 : 400, margin: 0 }}>
                      {isCore ? `${mod.label} module` : f.label}
                    </label>
                    <div className="form-check form-switch" style={{ minWidth: 44 }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={id}
                        checked={busy && pending ? pending.next : !!f.is_enabled}
                        disabled={!!saving || (!isCore && !coreOn)}
                        onChange={(e) => toggle(m, f, e.target.checked)}
                        style={{ cursor: busy ? "wait" : "pointer" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}
